import { useEffect, useState } from 'react'
import { Modal, Button, Spinner } from 'react-bootstrap'
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { toast } from 'react-toastify'

interface Setting {
  orgSettingID: number
  settingKey: string
  settingValue: string
  settingDetail: string
  isActive: boolean
  orgSettingGroupId: number
}

interface OrgSettingGroupModalProps {
  show: boolean
  onClose: () => void
  onSave: (data: any) => Promise<any>
  editingItem: any
}

const OrgSettingGroupModal = ({ show, onClose, onSave, editingItem }: OrgSettingGroupModalProps) => {
  const [formData, setFormData] = useState({
    groupName: '',
    description: '',
    isActive: true,
    settings: [] as Setting[],
  })
  const [errors, setErrors] = useState({
    groupName: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (editingItem) {
      const existingSettings = (editingItem.settings || []).map((s: any) => ({
        orgSettingID: s.orgSettingID || 0,
        settingKey: s.settingKey || '',
        settingValue: s.settingValue || '',
        settingDetail: s.settingDetail || '',
        isActive: s.isActive ?? true,
        orgSettingGroupId: editingItem.orgSettingGroupId || 0,
      }))
      setFormData({
        groupName: editingItem.groupName || '',
        description: editingItem.description || '',
        isActive: editingItem.isActive ?? true,
        settings: existingSettings,
      })
    } else {
      setFormData({
        groupName: '',
        description: '',
        isActive: true,
        settings: [],
      })
    }
    setErrors({ groupName: '' })
  }, [editingItem, show])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, type } = e.target
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement
      setFormData((prev) => ({ ...prev, [name]: target.checked }))
    } else {
      const { value } = e.target
      setFormData((prev) => ({ ...prev, [name]: value }))
    }
    if (name === 'groupName') {
      const value = e.target.value
      if (value.trim()) {
        setErrors((prev) => ({ ...prev, groupName: '' }))
      }
    }
  }

  const handleSettingChange = (index: number, field: keyof Setting, value: any) => {
    const updatedSettings = [...formData.settings]
    updatedSettings[index] = { ...updatedSettings[index], [field]: value }
    setFormData((prev) => ({ ...prev, settings: updatedSettings }))
  }

  const handleAddSetting = () => {
    const newSetting: Setting = {
      orgSettingID: 0,
      settingKey: '',
      settingValue: '',
      settingDetail: '',
      isActive: true,
      orgSettingGroupId: editingItem?.orgSettingGroupId || 0,
    }
    setFormData((prev) => ({ ...prev, settings: [...prev.settings, newSetting] }))
  }

  const handleRemoveSetting = (index: number) => {
    const updatedSettings = formData.settings.filter((_, i) => i !== index)
    setFormData((prev) => ({ ...prev, settings: updatedSettings }))
  }

  const validateForm = (): boolean => {
    const newErrors = { groupName: '' }
    let isValid = true

    if (!formData.groupName.trim()) {
      newErrors.groupName = 'Group Name is required'
      isValid = false
    }

    for (let i = 0; i < formData.settings.length; i++) {
      const setting = formData.settings[i]
      if (!setting.settingKey.trim()) {
        toast.error(`Setting at row ${i + 1} requires a key`)
        isValid = false
        break
      }
    }

    setErrors(newErrors)
    return isValid
  }

  const handleSubmit = async () => {
    if (!validateForm()) return
    if (isSubmitting) return

    setIsSubmitting(true)
    try {
      const payload = {
        orgSettingGroupId: editingItem?.orgSettingGroupId || 0,
        groupName: formData.groupName,
        description: formData.description,
        isActive: formData.isActive,
        settings: formData.settings.map(setting => ({
          orgSettingID: setting.orgSettingID,
          settingKey: setting.settingKey,
          settingValue: setting.settingValue,
          settingDetail: setting.settingDetail,
          isActive: setting.isActive,
          orgSettingGroupId: editingItem?.orgSettingGroupId || 0,
        })),
      }
      await onSave(payload)
    } catch (error) {
      // Error already handled in parent
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Tooltip id="remove-setting-tooltip" place="top" />
      <Modal show={show} onHide={onClose} size='xl' fullscreen={true} backdrop='static'>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingItem ? 'Edit Organization Setting Group' : 'Add New Organization Setting Group'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='row mb-5'>
            <div className='col-md-8'>
              <label className='required form-label'>Group Name</label>
              <input
                type='text'
                name='groupName'
                className={`form-control ${errors.groupName ? 'is-invalid' : ''}`}
                placeholder='Enter group name'
                value={formData.groupName}
                onChange={handleChange}
              />
              {errors.groupName && <div className='invalid-feedback'>{errors.groupName}</div>}
            </div>
            <div className='col-md-4'>
              <label className='form-label'>Status</label>
              <div className='form-check form-switch form-check-solid mt-2'>
                <input
                  type='checkbox'
                  name='isActive'
                  className='form-check-input'
                  checked={formData.isActive}
                  onChange={handleChange}
                  id='statusSwitch'
                />
                <label className='form-check-label' htmlFor='statusSwitch'>
                  {formData.isActive ? 'Active' : 'Inactive'}
                </label>
              </div>
            </div>
          </div>

          <div className='mb-5'>
            <label className='form-label'>Description</label>
            <textarea
              name='description'
              className='form-control'
              rows={2}
              placeholder='Enter description'
              value={formData.description}
              onChange={handleChange}
            />
          </div>

          <hr className='my-5' />
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <label className='form-label fw-bold'>Settings</label>
            <button type='button' className='btn btn-sm btn-primary' onClick={handleAddSetting}>
              <i className='fa fa-plus fs-4'></i>
              Add Setting
            </button>
          </div>

          <div className='table-responsive'>
            <table className='table table-bordered table-row-dashed align-middle'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th style={{ width: '25%' }}>Setting Key *</th>
                  <th style={{ width: '30%' }}>Setting Value</th>
                  <th style={{ width: '30%' }}>Setting Detail</th>
                  <th style={{ width: '7%' }} className='text-center'>Active</th>
                  <th style={{ width: '8%' }} className='text-center'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {formData.settings.length === 0 ? (
                  <tr>
                    <td colSpan={5} className='text-center text-muted'>No settings added. Click "Add Setting" to create one.</td>
                  </tr>
                ) : (
                  formData.settings.map((setting, idx) => (
                    <tr key={setting.orgSettingID !== 0 ? setting.orgSettingID : `new-${idx}`}>
                      <td>
                        <div className="input-group input-group-sm">
                          <input
                            type='text'
                            className='form-control form-control-sm'
                            placeholder='e.g., themeColor'
                            value={setting.settingKey}
                            disabled={setting.orgSettingID !== 0}
                            onChange={(e) => handleSettingChange(idx, 'settingKey', e.target.value)}
                            title={setting.settingKey}
                          />
                          <button
                            type="button"
                            className="btn btn-icon btn-success"
                            onClick={() => navigator.clipboard.writeText(setting.settingKey)}
                            title="Copy to clipboard"
                          >
                            <i className="fa fa-copy fs-6"></i>
                          </button>
                        </div>
                      </td>
                      <td>
                        <div className="input-group input-group-sm">
                          <textarea
                            className='form-control form-control-sm'
                            placeholder='Value'
                            value={setting.settingValue}
                            onChange={(e) => handleSettingChange(idx, 'settingValue', e.target.value)}
                            title={setting.settingValue}
                            rows={1}
                            style={{ resize: 'vertical', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                          />
                          <button
                            type="button"
                            className="btn btn-icon btn-success"
                            onClick={() => navigator.clipboard.writeText(setting.settingValue)}
                            title="Copy to clipboard"
                          >
                            <i className="fa fa-copy fs-6"></i>
                          </button>
                        </div>
                      </td>
                      <td>
                        <textarea
                          className='form-control form-control-sm'
                          placeholder='Additional detail'
                          value={setting.settingDetail}
                          onChange={(e) => handleSettingChange(idx, 'settingDetail', e.target.value)}
                          rows={1}
                          style={{ resize: 'vertical', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                        />
                      </td>
                      <td className='text-center align-middle'>
                        <input
                          type='checkbox'
                          className='form-check-input'
                          checked={setting.isActive}
                          onChange={(e) => handleSettingChange(idx, 'isActive', e.target.checked)}
                        />
                      </td>
                      <td className='text-center align-middle'>
                        <button
                          type='button'
                          className='btn btn-sm btn-icon btn-light-danger'
                          onClick={() => handleRemoveSetting(idx)}
                          data-tooltip-id="remove-setting-tooltip"
                          data-tooltip-content="Remove Setting"
                        >
                          <i className='fa fa-trash-alt fs-4'></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner as='span' animation='border' size='sm' role='status' aria-hidden='true' className='me-2' />
                {editingItem ? 'Updating...' : 'Saving...'}
              </>
            ) : (
              editingItem ? 'Update' : 'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default OrgSettingGroupModal